import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, UserPlus, RefreshCw, CheckCircle, XCircle, Loader2, Search, Filter, Mail, Eye, EyeOff, KeyRound, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { companyAPI, mailAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';

const LIMIT = 20;

export default function ZimbraUserManagementTab({ companyId }) {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [provisioningUser, setProvisioningUser] = useState(null);
    const [loadingActions, setLoadingActions] = useState({});
    
    // Server-side pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalMembers, setTotalMembers] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    
    // Stats from server
    const [stats, setStats] = useState({ total: 0, with_zimbra: 0, without_zimbra: 0 });
    
    // Search & Filter
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all, has_account, no_account
    const searchTimeoutRef = useRef(null);
    
    // Reveal Password dialog state
    const [revealDialogOpen, setRevealDialogOpen] = useState(false);
    const [revealAccountId, setRevealAccountId] = useState(null);
    const [revealUserName, setRevealUserName] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [revealLoading, setRevealLoading] = useState(false);
    const [revealedPassword, setRevealedPassword] = useState(null);
    const [revealedEmail, setRevealedEmail] = useState('');
    const [showRevealedPassword, setShowRevealedPassword] = useState(false);

    // Load stats once (not affected by filters)
    const loadStats = useCallback(async () => {
        if (!companyId) return;
        try {
            const response = await companyAPI.getMembers(companyId, {
                page: 1,
                limit: 1,
                with_zimbra: true,
            });
            if (response.data?.stats) {
                setStats({
                    total: response.data.stats.total || 0,
                    with_zimbra: response.data.stats.with_zimbra || 0,
                    without_zimbra: response.data.stats.without_zimbra || 0,
                });
            }
        } catch (error) {
            console.error('Failed to load stats:', error);
        }
    }, [companyId]);

    const loadMembers = useCallback(async (page = 1, search = '', filter = 'all') => {
        if (!companyId) return;
        try {
            setLoading(true);
            const params = {
                page,
                limit: LIMIT,
                search: search || undefined,
                with_zimbra: true,
                zimbra_status: filter !== 'all' ? (filter === 'has_account' ? 'has_account' : 'no_account') : undefined,
            };
            // Remove undefined params
            Object.keys(params).forEach(k => params[k] === undefined && delete params[k]);
            
            const response = await companyAPI.getMembers(companyId, params);
            if (response.data) {
                setMembers(response.data.items || []);
                setTotalMembers(response.data.total || 0);
                setHasMore(response.data.has_more || false);
            }
        } catch (error) {
            console.error('Failed to load members:', error);
            toast.error('Gagal memuat daftar member');
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    // Load on mount and when companyId changes
    useEffect(() => {
        setCurrentPage(1);
        loadStats();
        loadMembers(1, searchQuery, statusFilter);
    }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounced search
    const handleSearchChange = (value) => {
        setSearchQuery(value);
        setCurrentPage(1);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            loadMembers(1, value, statusFilter);
        }, 400);
    };

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setCurrentPage(1);
        loadMembers(1, searchQuery, value);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        loadMembers(newPage, searchQuery, statusFilter);
    };

    const handleProvisionUser = async (userId, userName) => {
        try {
            setProvisioningUser(userId);
            const response = await companyAPI.provisionZimbraUser(companyId, userId);
            
            if (response.data) {
                toast.success(`Zimbra account dibuat untuk ${userName}: ${response.data.email}`);
                await loadMembers(currentPage, searchQuery, statusFilter);
            }
        } catch (error) {
            console.error('Failed to provision user:', error);
            const errorMsg = error.response?.data?.detail || 'Gagal membuat Zimbra account';
            toast.error(errorMsg);
        } finally {
            setProvisioningUser(null);
        }
    };

    const handleTestSMTP = async (accountId) => {
        try {
            setLoadingActions(prev => ({ ...prev, [accountId]: { ...prev[accountId], testSMTP: true } }));
            const response = await mailAPI.testZimbraSMTP(accountId);
            
            if (response.data) {
                toast.success(`SMTP test completed: ${response.data.message || 'Success'}`);
            }
        } catch (error) {
            console.error('Failed to test SMTP:', error);
            const errorMsg = error.response?.data?.detail || 'SMTP test failed';
            toast.error(errorMsg);
        } finally {
            setLoadingActions(prev => ({ ...prev, [accountId]: { ...prev[accountId], testSMTP: false } }));
        }
    };

    const handleResync = async (accountId, userName) => {
        try {
            setLoadingActions(prev => ({ ...prev, [accountId]: { ...prev[accountId], resync: true } }));
            const response = await mailAPI.syncZimbraPassword(accountId);

            if (response.data) {
                const newPassword = response.data.new_password;
                toast.success(`Password berhasil di-reset untuk ${userName}`, {
                    description: newPassword ? `Password baru: ${newPassword}` : undefined,
                    duration: 10000,
                });
            }
        } catch (error) {
            console.error('Failed to sync password:', error);
            const errorMsg = error.response?.data?.detail || 'Re-sync Password gagal';
            toast.error(errorMsg);
        } finally {
            setLoadingActions(prev => ({ ...prev, [accountId]: { ...prev[accountId], resync: false } }));
        }
    };

    // ── Reveal Password ──
    const openRevealDialog = (accountId, userName) => {
        setRevealAccountId(accountId);
        setRevealUserName(userName);
        setConfirmPassword('');
        setRevealedPassword(null);
        setRevealedEmail('');
        setShowRevealedPassword(false);
        setRevealDialogOpen(true);
    };

    const handleRevealPassword = async () => {
        if (!confirmPassword.trim()) {
            toast.error('Masukkan password akun Anda untuk konfirmasi');
            return;
        }
        try {
            setRevealLoading(true);
            const response = await mailAPI.revealZimbraPassword(revealAccountId, confirmPassword);
            if (response.data) {
                setRevealedPassword(response.data.password);
                setRevealedEmail(response.data.email);
            }
        } catch (error) {
            const detail = error.response?.data?.detail || 'Gagal mengambil password';
            toast.error(detail);
        } finally {
            setRevealLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Password disalin ke clipboard');
        });
    };

    const handleSyncAll = async () => {
        try {
            setSyncing(true);
            const response = await companyAPI.syncAllZimbraUsers(companyId);
            
            if (response.data) {
                const { provisioned, already_exists, failed } = response.data;
                
                if (provisioned.length > 0) {
                    toast.success(`${provisioned.length} Zimbra account berhasil dibuat`);
                }
                if (already_exists.length > 0) {
                    toast.info(`${already_exists.length} user sudah memiliki Zimbra account`);
                }
                if (failed.length > 0) {
                    toast.error(`${failed.length} user gagal di-provision`);
                }
                
                await loadMembers(currentPage, searchQuery, statusFilter);
            }
        } catch (error) {
            console.error('Failed to sync users:', error);
            const errorMsg = error.response?.data?.detail || 'Gagal sync users';
            toast.error(errorMsg);
        } finally {
            setSyncing(false);
        }
    };

    const totalPages = Math.ceil(totalMembers / LIMIT);

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
        <div className="space-y-6">
            {/* Stats Cards — from server stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-brand-text-secondary">Total Members</p>
                                <p className="text-2xl font-bold text-brand-text-primary">{stats.total}</p>
                            </div>
                            <Users className="w-8 h-8 text-brand-text-tertiary" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-brand-text-secondary">Punya Akun Zimbra</p>
                                <p className="text-2xl font-bold text-green-600">{stats.with_zimbra}</p>
                            </div>
                            <CheckCircle className="w-8 h-8 text-green-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-brand-text-secondary">Belum Punya Akun</p>
                                <p className="text-2xl font-bold text-orange-600">{stats.without_zimbra}</p>
                            </div>
                            <XCircle className="w-8 h-8 text-orange-500" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Main Card */}
            <Card>
                <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <CardTitle>Zimbra Email Accounts</CardTitle>
                            <p className="text-sm text-brand-text-secondary mt-1">
                                Kelola akun email Zimbra untuk member company.
                            </p>
                        </div>
                        <Button
                            onClick={handleSyncAll}
                            disabled={syncing}
                            className="bg-brand-primary hover:bg-brand-primary-hover text-white"
                        >
                            {syncing ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
                            ) : (
                                <><RefreshCw className="w-4 h-4 mr-2" /> Sync All Users</>
                            )}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Search and Filter */}
                    <div className="flex flex-col sm:flex-row gap-3 mb-6">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-text-muted" />
                            <Input
                                value={searchQuery}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                placeholder="Cari nama atau email..."
                                className="pl-9"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                            <SelectTrigger className="w-full sm:w-[180px]">
                                <Filter className="w-4 h-4 mr-2" />
                                <SelectValue placeholder="Filter status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua</SelectItem>
                                <SelectItem value="has_account">Punya Akun</SelectItem>
                                <SelectItem value="no_account">Belum Punya</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Loading */}
                    {loading && (
                        <div className="text-center py-8">
                            <Loader2 className="w-6 h-6 inline animate-spin text-brand-primary" />
                            <p className="text-sm text-brand-text-muted mt-2">Memuat members...</p>
                        </div>
                    )}

                    {/* Empty state */}
                    {!loading && members.length === 0 && (
                        <div className="text-center py-8">
                            <Users className="w-12 h-12 mx-auto text-brand-text-tertiary mb-3" />
                            <p className="text-brand-text-secondary">
                                {searchQuery || statusFilter !== 'all'
                                    ? 'Tidak ada member yang cocok dengan filter.'
                                    : 'Belum ada member di company ini.'}
                            </p>
                        </div>
                    )}

                    {/* Member list */}
                    {!loading && members.length > 0 && (
                        <>
                            <div className="space-y-3">
                                {members.map((member) => {
                                    const userEmail = member.user_email || member.email || '';
                                    const userName = member.user_name || member.name || '(unknown)';
                                    const zimbraEmail = member.zimbra_email;
                                    const accountId = member.zimbra_account_id;
                                    const isProvisioning = provisioningUser === member.user_id;

                                    return (
                                        <div key={member.user_id} className="flex items-center justify-between p-3 rounded-lg border border-brand-border hover:bg-brand-background/50 transition-colors">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 rounded-full bg-brand-primary text-white flex items-center justify-center font-medium text-sm flex-shrink-0">
                                                    {getInitials(userName)}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium text-brand-text-primary text-sm truncate">{userName}</span>
                                                        {member.role === 'owner' && <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">Owner</Badge>}
                                                    </div>
                                                    <div className="text-xs text-brand-text-muted truncate">{userEmail}</div>
                                                    {zimbraEmail && (
                                                        <div className="flex items-center gap-1 mt-0.5">
                                                            <Mail className="w-3 h-3 text-green-500" />
                                                            <span className="text-xs text-green-600">{zimbraEmail}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                                {zimbraEmail ? (
                                                    <>
                                                        <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200">
                                                            <CheckCircle className="w-3 h-3 mr-1" />
                                                            Aktif
                                                        </Badge>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => openRevealDialog(accountId, userName)}
                                                            className="text-brand-text-muted"
                                                        >
                                                            <Eye className="w-4 h-4 mr-1" />
                                                            Password
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleResync(accountId, userName)}
                                                            disabled={loadingActions[accountId]?.resync}
                                                        >
                                                            {loadingActions[accountId]?.resync ? (
                                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            ) : (
                                                                <RefreshCw className="w-4 h-4 mr-2" />
                                                            )}
                                                            Re-Sync Password
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Badge variant="secondary">
                                                            <XCircle className="w-3 h-3 mr-1" />
                                                            Belum Ada Akun
                                                        </Badge>
                                                        <Button
                                                            size="sm"
                                                            onClick={() => handleProvisionUser(member.user_id, userName)}
                                                            disabled={isProvisioning}
                                                        >
                                                            {isProvisioning ? (
                                                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                                                            ) : (
                                                                <><UserPlus className="w-4 h-4 mr-2" /> Create Account</>
                                                            )}
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between mt-6 pt-4 border-t border-brand-border">
                                    <p className="text-sm text-brand-text-secondary">
                                        Menampilkan {(currentPage - 1) * LIMIT + 1}–{Math.min(currentPage * LIMIT, totalMembers)} dari {totalMembers} members
                                    </p>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage <= 1 || loading}
                                        >
                                            Previous
                                        </Button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                
                                                return (
                                                    <Button
                                                        key={pageNum}
                                                        variant={currentPage === pageNum ? 'default' : 'outline'}
                                                        size="sm"
                                                        onClick={() => handlePageChange(pageNum)}
                                                        disabled={loading}
                                                    >
                                                        {pageNum}
                                                    </Button>
                                                );
                                            })}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage >= totalPages || loading}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Reveal Password Dialog */}
            <Dialog open={revealDialogOpen} onOpenChange={setRevealDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Lihat Password Zimbra</DialogTitle>
                        <DialogDescription>
                            Masukkan password akun Anda untuk melihat password Zimbra {revealUserName}.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {!revealedPassword ? (
                        <>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Password Akun Anda</label>
                                    <div className="relative">
                                        <Input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Masukkan password Anda..."
                                            onKeyDown={(e) => e.key === 'Enter' && handleRevealPassword()}
                                        />
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setRevealDialogOpen(false)}>Batal</Button>
                                <Button onClick={handleRevealPassword} disabled={revealLoading}>
                                    {revealLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : <><Eye className="w-4 h-4 mr-2" /> Lihat Password</>}
                                </Button>
                            </DialogFooter>
                        </>
                    ) : (
                        <>
                            <div className="space-y-4 py-4">
                                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <p className="text-sm font-medium text-green-700 mb-1">Email Zimbra</p>
                                    <p className="text-sm text-green-600 mb-3">{revealedEmail}</p>
                                    <p className="text-sm font-medium text-green-700 mb-1">Password</p>
                                    <div className="flex items-center gap-2">
                                        <code className="text-sm bg-white px-2 py-1 rounded border font-mono">
                                            {showRevealedPassword ? revealedPassword : '••••••••'}
                                        </code>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setShowRevealedPassword(!showRevealedPassword)}
                                        >
                                            {showRevealedPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => copyToClipboard(revealedPassword)}
                                        >
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                <p className="text-xs text-brand-text-muted">
                                    <KeyRound className="w-3 h-3 inline mr-1" />
                                    Simpan password ini dengan aman. Password tidak akan ditampilkan lagi.
                                </p>
                            </div>
                            <DialogFooter>
                                <Button onClick={() => setRevealDialogOpen(false)}>Tutup</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}